import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { addToDate } from '@gamiphy/utils/date';
import {
  EcommerceHitEventActions,
  Hit,
  OrderData,
  PredefinedHitCategories,
  SessionHitEvent,
  SessionHitSite,
  SessionHitTypes,
} from '@gamiphy/bl/analytics';
import { Http } from '@gamiphy/http';

export class Analytics {
  private static readonly SESSION_COOKIE_NAME = '_gamiphy_sid';

  private static readonly CUSTOMER_COOKIE_NAME = '_gamiphy_cid';

  /**
   * Session timeout in seconds
   * 30 minutes as GA default (https://support.google.com/analytics/answer/2795871)
   * @private
   */
  private static readonly SESSION_TIMEOUT_MINUTES = 30;

  /**
   * Customer cookie timeout
   * We cannot leave expires blank, else the cookie will be removed after the browser being closed
   * So we set the cookie 2 years long
   * @private
   */
  private static readonly CUSTOMER_TIMEOUT_YEARS = 2;

  private userId?: string = undefined;

  constructor() {
    // Collect page view event
    void this.collect({
      type: SessionHitTypes.pageView,
    });
  }

  public get customerId(): string {
    return Analytics.getCustomerId();
  }

  /**
   * Get customer id, create if not exist
   * @private
   */
  private static getCustomerId(): string {
    const customerId = Cookies.get(Analytics.CUSTOMER_COOKIE_NAME);
    if (customerId) {
      return customerId;
    }

    const newCustomerId = uuidv4();
    Cookies.set(Analytics.CUSTOMER_COOKIE_NAME, newCustomerId, {
      expires: addToDate(new Date(), 'years', Analytics.CUSTOMER_TIMEOUT_YEARS),
    });

    return newCustomerId;
  }

  /**
   * Get session id, create if not exist, update expiry if exists
   */
  private static getSessionId(): string {
    const sessionId = Cookies.get(Analytics.SESSION_COOKIE_NAME) || uuidv4();

    // Create cookie if not exists or update expiry if exist
    Cookies.set(Analytics.SESSION_COOKIE_NAME, sessionId, {
      expires: addToDate(new Date(), 'minutes', Analytics.SESSION_TIMEOUT_MINUTES),
    });

    return sessionId;
  }

  /**
   * Get session site data
   * @private
   */
  private static getSiteData(): SessionHitSite {
    return {
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      pageTitle: document.title,
      browserSize: `${window.innerWidth}x${window.innerHeight}`,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
    };
  }

  /**
   * Set user id to the session
   * @param userId User id value
   */
  public async setUserId(userId: string): Promise<void> {
    this.userId = userId;

    await this.collect();
  }

  /**
   * Collect
   * @param hit Hit data
   */
  public async collect(hit?: Omit<Hit, 'site'>): Promise<void> {
    await Http.post({
      url: '/v3/analytics/collector/collect',
      payload: {
        sessionId: Analytics.getSessionId(),
        customerId: this.customerId,
        userId: this.userId,
        ...(hit && {
          hit: {
            ...hit,
            site: Analytics.getSiteData(),
          },
        }),
      },
    });
  }

  public async collectEvent(event: SessionHitEvent): Promise<void> {
    await this.collect({
      type: SessionHitTypes.event,
      event,
    });
  }

  /**
   * Collect product view event
   * @param externalProductId store product id
   */
  public async collectProductView(externalProductId: string): Promise<void> {
    await this.collectEvent({
      category: PredefinedHitCategories.ecommerce,
      action: EcommerceHitEventActions.productView,
      attributes: {
        externalProductId,
      },
    });
  }

  /**
   * Collect product add to cart event
   * @param externalProductId store product id
   */
  public async collectAddToCart(externalProductId: string): Promise<void> {
    await this.collectEvent({
      category: PredefinedHitCategories.ecommerce,
      action: EcommerceHitEventActions.addToCart,
      attributes: {
        externalProductId,
      },
    });
  }

  /**
   * Collect order place event
   * @param externalOrderId store order id
   * @param data
   */
  public async collectOrderPlace(externalOrderId: string, data?: OrderData): Promise<void> {
    await this.collectEvent({
      category: PredefinedHitCategories.ecommerce,
      action: EcommerceHitEventActions.orderPlace,
      attributes: {
        externalOrderId,
        externalProductIds: data?.items?.map((item) => item.id)
          .join(','),
        total: data?.total,
      },
    });
  }
}
